import type { GeoFilterGroups } from './analyticsMap'
import type { FilterGroupRequest } from './contactList'

// Re-export so downstream imports from geoAnalytics continue to resolve.
export type { GeoFilterGroups }

export type GeoUnitTypeKey =
  | 'precinct'
  | 'censusTract'
  | 'censusBlockGroup'
  | 'county'
  | 'congressionalDistrict'
  | 'stateSenateDistrict'
  | 'stateHouseDistrict'
  | 'ward'
  | 'municipality'

// Geographies the backend live-metrics endpoint accepts (union footprint/leverage + "my universe"
// all route through it). Keep in lockstep with GeoAnalyticsService.SupportsLiveMetrics — selecting a
// live-metrics layer on any OTHER geography 400s and silently blanks the map, so the picker gates on this.
// Precinct/tract/block group join on voter-file codes; county + districts use spatial containment.
export const LIVE_METRIC_UNIT_TYPES: GeoUnitTypeKey[] = [
  'precinct', 'censusTract', 'censusBlockGroup',
  'county', 'congressionalDistrict', 'stateSenateDistrict', 'stateHouseDistrict',
]

export function supportsLiveMetrics(unitType: GeoUnitTypeKey): boolean {
  return LIVE_METRIC_UNIT_TYPES.includes(unitType)
}

/**
 * DT-PROD-7(i): the acting committee's default map DISTRICT type — the backend-fixed
 * "Congressional" | "StateSenate" | "StateHouse" vocabulary (GeoAnalyticsController's
 * district-type parse; set as a type+code PAIR on the scope-config page) — mapped to the
 * Geography the analytics map should OPEN on.
 *
 * Anything else falls back to 'precinct': no default district ("Whole state"), no scope
 * config, config not loaded yet, or an unrecognised value. Never 'censusTract': opening on
 * the densest geography was the DT-PROD-7(i) complaint.
 *
 * District geographies support live metrics (spatial containment, 2026-10-23 — see
 * LIVE_METRIC_UNIT_TYPES), so a district default keeps Union Footprint / "my universe" available.
 */
export function defaultUnitTypeFor(districtType?: string | null): GeoUnitTypeKey {
  switch (districtType) {
    case 'Congressional': return 'congressionalDistrict'
    case 'StateSenate': return 'stateSenateDistrict'
    case 'StateHouse': return 'stateHouseDistrict'
    default: return 'precinct'
  }
}

/**
 * 2026-10-23 bug 7: the State the analytics map opens on. The committee's explicit default map
 * state wins; otherwise, when the locality grant (GET /api/config/available-states, already
 * narrowed server-side to the acting committee's grant) covers EXACTLY one state, open there
 * instead of an empty CONUS view. Anything else (unrestricted / multi-state / not loaded) → null.
 */
export function initialMapStateFor(
  defaultMapState: string | null | undefined,
  availableStates: ReadonlyArray<{ code: string }> | null | undefined,
): string | null {
  if (defaultMapState) return defaultMapState
  if (availableStates && availableStates.length === 1) return availableStates[0].code
  return null
}

export interface GeoUnitsParams {
  type: GeoUnitTypeKey
  state: string
  vintage?: number
  bbox?: string
  /** Data-version token from useGeoUnitsVersion — busts the long-lived HTTP cache after an ingest. */
  v?: string
}

export interface GeoMetricRow {
  geoUnitId: string
  metricKey: string
  value: number | null
  suppressed: boolean
}

export interface GeoMetricsRequest {
  unitType: GeoUnitTypeKey
  state: string
  vintageYear?: number
  metricKeys: string[]
}

export interface GeoLiveMetricsRequest {
  unitType: GeoUnitTypeKey
  state: string
  vintageYear?: number
  filterGroups: GeoFilterGroups
  ratioDenominatorMetricKey?: string
}

export interface LeanCyclePoint {
  year: number
  lean: number
}

export interface TurnoutPoint {
  year: number
  value: number | null
  suppressed: boolean
}

export interface DropoffPoint {
  targetYear: number
  value: number | null
  suppressed: boolean
}

export interface GeoUnitProfile {
  id: string
  code: string
  name: string
  unitType: GeoUnitTypeKey
  state: string
  lean: number | null
  leanFormatted: string | null
  leanByCycle: LeanCyclePoint[]
  turnout: TurnoutPoint[]
  dropoff: DropoffPoint[]
  boundaryWkt: string
}

export interface ContestResultRow {
  year: number
  office: string
  demSharePct: number
  repSharePct: number
  marginPp: number
}

export interface FlagRollupRow {
  flagKey: string
  count: number | null
  suppressed: boolean
}

export interface UnionFootprint {
  members: number | null
  membersSuppressed: boolean
  worksites: number
  stewards: number
  leverageIndexPct: number | null
}

export interface WorksiteRow {
  worksiteId: string
  label: string
  type: string | null
  city: string | null
  unions: string[]
  hasSteward: boolean
}

export interface EmployerGroup {
  businessId: string
  businessName: string
  worksites: WorksiteRow[]
}

export interface UnionFootprintDetail {
  employers: EmployerGroup[]
  independentWorksites: WorksiteRow[]
  worksiteCount: number
}

export interface GeoUnitFullProfile {
  profile: GeoUnitProfile
  results: ContestResultRow[]
  acs: { metricKey: string; value: number | null }[]
  flagRollups: FlagRollupRow[]
  union: UnionFootprint
  metricsComputedAt: string | null
}

export type MetricGroup = 'elections' | 'demographics' | 'terrain' | 'union'

export interface MetricOption {
  key: string // metric-key prefix; buildMetricKey appends ".{year}" when requiresYear
  label: string
  kind: 'diverging' | 'sequential'
  domain: [number, number]
  requiresYear?: boolean
  yearOptions?: number[]
  format: 'lean' | 'percent' | 'count'
  description: string
  group: MetricGroup
}

export const METRIC_OPTIONS: MetricOption[] = [
  // RENAME-ON-PUBLIC-NAME (WEB-15): "ORCL Lean" carries the placeholder product name and MUST be
  // renamed when the public name is decided. Do not rename piecemeal — work the checklist in
  // website/TODO.md → "Rename checklist — ORCL Lean" (app + site change together).
  {
    key: 'lean',
    label: 'ORCL Lean (partisan lean)',
    kind: 'diverging',
    domain: [-15, 15],
    format: 'lean',
    group: 'elections',
    description: 'Two-cycle presidential lean vs the national baseline. Blue = D lean, red = R lean.',
  },
  {
    key: 'turnout.general',
    label: 'Turnout — general election',
    kind: 'sequential',
    domain: [0, 100],
    requiresYear: true,
    yearOptions: [2020, 2022, 2024],
    format: 'percent',
    group: 'elections',
    description: 'Share of active registered voters who voted.',
  },
  {
    key: 'dropoff.midterm',
    label: 'Midterm drop-off',
    kind: 'sequential',
    domain: [0, 100],
    requiresYear: true,
    yearOptions: [2022],
    format: 'percent',
    group: 'elections',
    description: 'Presidential-year voters who skipped the midterm.',
  },

  // ---- demographics (ACS, slice 2) ----
  { key: 'acs.median_income', label: 'Median household income', kind: 'sequential', domain: [20000, 150000], format: 'count', group: 'demographics', description: 'ACS 5-year median household income (dollars).' },
  { key: 'acs.median_age', label: 'Median age', kind: 'sequential', domain: [20, 60], format: 'count', group: 'demographics', description: 'ACS 5-year median age.' },
  { key: 'acs.college_share', label: 'College degree (25+)', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'demographics', description: "Bachelor's degree or higher among adults 25+." },
  { key: 'acs.age18_29_share', label: 'Age 18–29', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Share of population aged 18–29.' },
  { key: 'acs.age65_share', label: 'Age 65+', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Share of population aged 65+.' },
  { key: 'acs.poverty_share', label: 'Below poverty line', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Share below the poverty line.' },
  { key: 'acs.renter_share', label: 'Renters', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'demographics', description: 'Renter-occupied housing share.' },
  { key: 'acs.race.white', label: 'White (non-Hispanic)', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'demographics', description: 'Share white, non-Hispanic.' },
  { key: 'acs.race.black', label: 'Black (non-Hispanic)', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'demographics', description: 'Share Black, non-Hispanic.' },
  { key: 'acs.race.hispanic', label: 'Hispanic or Latino', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'demographics', description: 'Share Hispanic or Latino, any race.' },
  { key: 'acs.race.asian', label: 'Asian (non-Hispanic)', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'demographics', description: 'Share Asian, non-Hispanic.' },
  { key: 'acs.industry.manufacturing', label: 'Works in manufacturing', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Employed residents in manufacturing.' },
  { key: 'acs.industry.construction', label: 'Works in construction', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Employed residents in construction.' },
  { key: 'acs.industry.education_health', label: 'Works in education/healthcare', kind: 'sequential', domain: [0, 60], format: 'percent', group: 'demographics', description: 'Employed residents in education, healthcare, social assistance.' },
  { key: 'acs.industry.retail', label: 'Works in retail', kind: 'sequential', domain: [0, 40], format: 'percent', group: 'demographics', description: 'Employed residents in retail trade.' },
  { key: 'acs.industry.transport_warehousing', label: 'Works in transport/warehousing', kind: 'sequential', domain: [0, 40], format: 'percent', group: 'demographics', description: 'Employed residents in transportation, warehousing, utilities.' },
  { key: 'acs.industry.public_admin', label: 'Works in public administration', kind: 'sequential', domain: [0, 30], format: 'percent', group: 'demographics', description: 'Employed residents in public administration.' },
  { key: 'acs.industry.professional', label: 'Works in professional services', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Employed residents in professional, scientific, management, admin.' },
  { key: 'acs.industry.arts_food', label: 'Works in hospitality/food', kind: 'sequential', domain: [0, 40], format: 'percent', group: 'demographics', description: 'Employed residents in arts, entertainment, accommodation, food.' },
  { key: 'acs.industry.finance_realestate', label: 'Works in finance/real estate', kind: 'sequential', domain: [0, 40], format: 'percent', group: 'demographics', description: 'Employed residents in finance, insurance, real estate.' },
  { key: 'acs.industry.wholesale', label: 'Works in wholesale', kind: 'sequential', domain: [0, 30], format: 'percent', group: 'demographics', description: 'Employed residents in wholesale trade.' },
  { key: 'acs.industry.information', label: 'Works in information', kind: 'sequential', domain: [0, 30], format: 'percent', group: 'demographics', description: 'Employed residents in the information sector.' },
  { key: 'acs.industry.agriculture_mining', label: 'Works in agriculture/mining', kind: 'sequential', domain: [0, 30], format: 'percent', group: 'demographics', description: 'Employed residents in agriculture, forestry, fishing, mining.' },
  { key: 'acs.industry.other_services', label: 'Works in other services', kind: 'sequential', domain: [0, 30], format: 'percent', group: 'demographics', description: 'Employed residents in other services (except public administration).' },
  { key: 'acs.occupation.management_professional', label: 'Management/professional occupations', kind: 'sequential', domain: [0, 70], format: 'percent', group: 'demographics', description: 'Management, business, science, arts occupations.' },
  { key: 'acs.occupation.service', label: 'Service occupations', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Service occupations.' },
  { key: 'acs.occupation.production_transport', label: 'Production/transport occupations', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Production, transportation, material moving.' },
  { key: 'acs.occupation.sales_office', label: 'Sales and office occupations', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Sales and office occupations.' },
  { key: 'acs.occupation.construction_maintenance', label: 'Construction, maintenance occupations', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'demographics', description: 'Natural resources, construction, and maintenance occupations.' },

  // ---- political terrain (Catalist, ship-gated server-side; absent from metric-keys until enabled) ----
  { key: 'catalist.persuadable_share', label: 'Persuadable share (turnable band)', kind: 'sequential', domain: [0, 60], format: 'percent', group: 'terrain', description: 'Share of voters in the ideology mid-band (default 35–65) — the ORCL turnable band.' },
  { key: 'catalist.issue.choice', label: 'Pro-choice terrain', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'terrain', description: 'Share likely/very likely to support abortion rights.' },
  { key: 'catalist.issue.climate', label: 'Climate terrain', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'terrain', description: 'Share likely/very likely to support climate action.' },
  { key: 'catalist.issue.guns', label: 'Gun-control terrain', kind: 'sequential', domain: [0, 100], format: 'percent', group: 'terrain', description: 'Share likely/very likely to support gun control.' },
  { key: 'catalist.activism_share', label: 'Activist density (share)', kind: 'sequential', domain: [0, 50], format: 'percent', group: 'terrain', description: 'Share of voters scoring high on general political activism — an activism-terrain view with no election/ACS equivalent.' },
]

export const UNIVERSE_METRIC_OPTION: MetricOption = {
  key: 'universe.count',
  label: 'My universe (contact count)',
  kind: 'sequential',
  domain: [0, 100], // replaced dynamically by computeDomain()
  format: 'count',
  group: 'union',
  description: 'Contacts matching the current filters, counted per area.',
}

export function buildMetricKey(option: MetricOption, year: number | null): string {
  return option.requiresYear && year != null ? `${option.key}.${year}` : option.key
}

/** Preset universe: contacts with an active union employment (existing filter engine field). */
export const UNION_UNIVERSE_FILTER_GROUPS: FilterGroupRequest[] = [
  {
    groupIndex: 0,
    filters: [{ fieldName: 'IsUnionEmployee', operator: 'Equals', value: 'true', displayOrder: 0 }],
  },
]

export const UNION_LAYER_OPTIONS: MetricOption[] = [
  { key: 'union.footprint', label: 'Union footprint (members)', kind: 'sequential', domain: [0, 100], format: 'count', group: 'union', description: "Your committee's tracked union members per area." },
  { key: 'union.leverage', label: 'Union leverage index', kind: 'sequential', domain: [0, 30], format: 'percent', group: 'union', description: 'Tracked members ÷ registered voters per area.' },
]

export const METRIC_GROUP_LABELS: Record<MetricGroup, string> = {
  elections: 'Elections',
  demographics: 'Demographics (ACS)',
  terrain: 'Political terrain',
  union: 'Union',
}

export interface UnitTypeOption {
  key: GeoUnitTypeKey
  label: string
}

// No vintage here on purpose: the client never pins a boundary vintage. The geo endpoints resolve
// it server-side (MAX(VintageYear) per unitType+state), so a stale client constant can't mismatch
// the ingested data and blank the map (see the VEST-2020-vs-2024 precinct bug). Add a vintage
// PICKER here only if/when multiple vintages per geography are ingested and the user must choose.
export const UNIT_TYPE_OPTIONS: UnitTypeOption[] = [
  { key: 'censusTract', label: 'Census tracts' },
  { key: 'censusBlockGroup', label: 'Block groups' },
  { key: 'precinct', label: 'Precincts' },
  { key: 'county', label: 'Counties' },
  { key: 'congressionalDistrict', label: 'Congressional districts' },
  { key: 'stateSenateDistrict', label: 'State senate districts' },
  { key: 'stateHouseDistrict', label: 'State house districts' },
]

export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
]

/**
 * Static per-state bounding boxes for the analytics-map camera fit (Area F).
 * Shape matches what map.fitBounds consumes elsewhere on this page
 * (featureCollectionBounds / listBounds): [[west, south], [east, north]] =
 * [[minLng, minLat], [maxLng, maxLat]] — assignable to maplibre's LngLatBoundsLike.
 *
 * These are approximate state extents (WGS84), good enough to frame the state on
 * selection. Decoupling the state fly-to from the metric-gated units query means a
 * state selection flies immediately, before (or without) any metric being picked.
 */
export type StateBBox = [[number, number], [number, number]]

/**
 * National two-party Democratic share of the presidential vote, by cycle (percent).
 * Mirror of backend LeanCalculator.NationalTwoPartyDemShare (source of truth).
 * Extend when a new presidential cycle is certified.
 */
export const NATIONAL_TWO_PARTY_DEM_SHARE: Record<number, number> = {
  2016: 51.11,
  2020: 52.27,
  2024: 49.25,
}

export const US_STATE_BOUNDS: Record<string, StateBBox> = {
  AL: [[-88.47, 30.22], [-84.89, 35.01]],
  AK: [[-168.0, 54.0], [-129.0, 71.5]],
  AZ: [[-114.82, 31.33], [-109.05, 37.0]],
  AR: [[-94.62, 33.0], [-89.64, 36.5]],
  CA: [[-124.48, 32.53], [-114.13, 42.01]],
  CO: [[-109.06, 36.99], [-102.04, 41.0]],
  CT: [[-73.73, 40.98], [-71.79, 42.05]],
  DE: [[-75.79, 38.45], [-75.05, 39.84]],
  DC: [[-77.12, 38.79], [-76.91, 39.0]],
  FL: [[-87.63, 24.52], [-80.03, 31.0]],
  GA: [[-85.61, 30.36], [-80.84, 35.0]],
  HI: [[-160.25, 18.91], [-154.81, 22.24]],
  ID: [[-117.24, 42.0], [-111.04, 49.0]],
  IL: [[-91.51, 36.97], [-87.02, 42.51]],
  IN: [[-88.1, 37.77], [-84.78, 41.76]],
  IA: [[-96.64, 40.38], [-90.14, 43.5]],
  KS: [[-102.05, 36.99], [-94.59, 40.0]],
  KY: [[-89.57, 36.5], [-81.96, 39.15]],
  LA: [[-94.04, 28.93], [-88.82, 33.02]],
  ME: [[-71.08, 43.06], [-66.95, 47.46]],
  MD: [[-79.49, 37.91], [-75.05, 39.72]],
  MA: [[-73.51, 41.24], [-69.93, 42.89]],
  MI: [[-90.42, 41.7], [-82.41, 48.31]],
  MN: [[-97.24, 43.5], [-89.49, 49.38]],
  MS: [[-91.66, 30.17], [-88.1, 35.0]],
  MO: [[-95.77, 35.99], [-89.1, 40.61]],
  MT: [[-116.05, 44.36], [-104.04, 49.0]],
  NE: [[-104.05, 40.0], [-95.31, 43.0]],
  NV: [[-120.01, 35.0], [-114.04, 42.0]],
  NH: [[-72.56, 42.7], [-70.61, 45.31]],
  NJ: [[-75.56, 38.93], [-73.89, 41.36]],
  NM: [[-109.05, 31.33], [-103.0, 37.0]],
  NY: [[-79.76, 40.5], [-71.86, 45.01]],
  NC: [[-84.32, 33.84], [-75.46, 36.59]],
  ND: [[-104.05, 45.94], [-96.55, 49.0]],
  OH: [[-84.82, 38.4], [-80.52, 41.98]],
  OK: [[-103.0, 33.62], [-94.43, 37.0]],
  OR: [[-124.57, 41.99], [-116.46, 46.29]],
  PA: [[-80.52, 39.72], [-74.69, 42.27]],
  RI: [[-71.86, 41.15], [-71.12, 42.02]],
  SC: [[-83.35, 32.03], [-78.54, 35.22]],
  SD: [[-104.06, 42.48], [-96.44, 45.95]],
  TN: [[-90.31, 34.98], [-81.65, 36.68]],
  TX: [[-106.65, 25.84], [-93.51, 36.5]],
  UT: [[-114.05, 36.99], [-109.04, 42.0]],
  VT: [[-73.44, 42.73], [-71.46, 45.02]],
  VA: [[-83.68, 36.54], [-75.24, 39.47]],
  WA: [[-124.85, 45.54], [-116.92, 49.0]],
  WV: [[-82.64, 37.2], [-77.72, 40.64]],
  WI: [[-92.89, 42.49], [-86.81, 47.31]],
  WY: [[-111.06, 40.99], [-104.05, 45.01]],
}
